import { ReactNode, useEffect, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  UniqueIdentifier,
  useDraggable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDroppable } from "@dnd-kit/core";
import {
  Box,
  Center,
  Grid,
  GridItem,
  SystemStyleObject,
  theme,
  useBreakpointValue,
} from "@chakra-ui/react";
import MarkDownRender from "../MarkDownRender";
import { swapKeyValue } from "./Quiz/helper";
import { IHightlighType } from "./Quiz/types";
import { colors } from "../../theme/tokens/colors";
import CodeHighlight from "./codeHightlight";

const placeholderBg = "whiteAlpha.300";

type IDragAndDropProps = {
  value?: Record<UniqueIdentifier, UniqueIdentifier | null>;
  draggableItems: { id: string; label: string }[];
  template: string;
  containers: string[];
  itemHeight?: string | number;
  itemWidth?: string | number;
  gridColumns?: number;
  disabled?: boolean;
  onDrag?: () => void;
  onChange?: (
    itemParents: Record<UniqueIdentifier, UniqueIdentifier | null>,
    parentsByItems: Record<UniqueIdentifier, UniqueIdentifier | null>
  ) => void;
  highlight?: IHightlighType;
  highlightTemplate?: boolean;
};

type IDraggableProps = {
  id: string;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  style?: SystemStyleObject;
};

type IDroppableProps = {
  id: string;
  children: ReactNode;
  style?: SystemStyleObject;
};

export function Droppable(props: IDroppableProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: props.id,
  });
  const style = {
    bg: isOver ? "yellow.500" : placeholderBg,
  };

  return (
    <Box
      sx={{
        // border: "1px solid white",
        borderRadius: ".5rem",
        ...props.style,
        ...style,
      }}
      ref={setNodeRef}
    >
      {props.children}
    </Box>
  );
}

export function Draggable({
  id,
  children,
  disabled,
  onClick,
  style: styleProps,
}: IDraggableProps) {
  const { attributes, listeners, setNodeRef, transform, active } = useDraggable(
    {
      id,
    }
  );
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const onDisableStyles = disabled
    ? { bg: theme.colors.gray[100], pointerEvents: "none" }
    : {
        bg: active?.id === id ? theme.colors.yellow[50] : "white",
      };

  return (
    <Box
      onClick={onClick}
      sx={{
        color: "black",
        borderRadius: ".4rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "500",
        border: `2px solid ${
          active?.id === id ? theme.colors.yellow[500] : "#ACAAA8"
        } `,
        boxShadow: `1px 2px 0px 0px ${
          active?.id === id ? theme.colors.yellow[500] : " #ACAAA8"
        } `,
        ...onDisableStyles,
        ...styleProps,
      }}
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
    >
      <Center>{children}</Center>
    </Box>
  );
}

const DragAndDrop = ({
  draggableItems,
  template,
  containers,
  itemHeight = "2rem",
  itemWidth = "8rem",
  onChange,
  disabled,
  value,
  onDrag,
  highlight = "neutral",
  gridColumns = 2,
  highlightTemplate = false,
}: IDragAndDropProps) => {
  const isSmallDevice = useBreakpointValue({ base: true, md: true, lg: false });
  const [itemsParent, setItemsParent] = useState<
    Record<UniqueIdentifier, UniqueIdentifier | null>
  >(() => (value ? value : {}));

  const sensors = useSensors(
    useSensor(isSmallDevice ? TouchSensor : PointerSensor, {
      activationConstraint: { distance: 0.01 },
    })
  );

  useEffect(() => {
    if (value) {
      setItemsParent(value);
    }
  }, [value]);

  useEffect(() => {
    if (onChange) {
      onChange(itemsParent, swapKeyValue(itemsParent));
    }
  }, [onChange, itemsParent]);

  const renderDroppables = () => {
    if (!template || typeof template !== "string") return null;

    const lines = template.split("\n");

    return (
      <Box
        p={"1rem"}
        mt={"1rem"}
        display="flex"
        flexDirection="column"
        justifyContent={"center"}
        alignItems="center"
        sx={{
          border: "2px dashed",
          borderColor:
            highlight === "neutral"
              ? "whiteAlpha.400"
              : highlight === "success"
              ? "green.600"
              : "yellow.600",
          borderRadius: ".2rem",
          bg:
            highlight === "neutral"
              ? "transparent"
              : highlight === "success"
              ? colors.shades.success
              : colors.shades.warning,
        }}
      >
        {lines.map((line, lineIndex) => {
          const parts = line.split(/({{DROPZONE(?::[^}]*?)?}})/);

          return (
            <Box
              key={lineIndex}
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              width="100%"
            >
              {parts.map((part, index) => {
                const inputMatch = part.match(
                  /{{DROPZONE(?::\s*([^:}]*?)\s*)?(?::\s*(\d+(?:px|rem|em|%)))?}}/
                );

                if (inputMatch) {
                  // const width = inputMatch[2] || "80px";
                  const containerId = inputMatch[1];
                  const style = { width: itemWidth, height: itemHeight };
                  return (
                    <Box key={containerId}>
                      <Droppable style={style} id={containerId}>
                        {draggableItems.map((item) =>
                          itemsParent[item.id] === containerId ? (
                            <Draggable
                              onClick={() => handleItemClick(item.id)}
                              key={item.id}
                              style={style}
                              disabled={disabled}
                              id={item.id}
                            >
                              {item.label}
                            </Draggable>
                          ) : null
                        )}
                        {/* {!Object.values(itemsParent).includes(containerId) &&
                          "Drop here"} */}
                      </Droppable>
                    </Box>
                  );
                }

                return part.trim() ? (
                  highlightTemplate ? (
                    <CodeHighlight
                      key={`${lineIndex}-${index}`}
                      code={part}
                      language="python"
                      style={{
                        paddingLeft: "0rem",
                        paddingRight: "0rem",
                        fontSize: "1rem",
                      }}
                    />
                  ) : (
                    <MarkDownRender
                      key={`${lineIndex}-${index}`}
                      content={part}
                    />
                  )
                ) : (
                  <span key={`${lineIndex}-${index}`}>&nbsp;</span>
                );
              })}
            </Box>
          );
        })}
      </Box>
    );
  };

  // const activeItem = activeId
  //   ? draggableItems.find((item) => item.id === activeId)
  //   : null;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* Render containers */}
      <Center>
        {/* {containers.map((containerId) => (
          <Droppable key={containerId} id={containerId}>
            {draggableItems.map((item) =>
              itemsParent[item.id] === containerId ? (
                <Draggable
                  onClick={() => handleItemClick(item.id)}
                  key={item.id}
                  id={item.id}
                >
                  {item.content}
                </Draggable>
              ) : null
            )}
            {!Object.values(itemsParent).includes(containerId) && "Drop here"}
          </Droppable>
        ))} */}
        {renderDroppables()}
      </Center>
      {/* {createPortal(
        <DragOverlay>{activeId && activeItem ? <></> : null}</DragOverlay>,
        document.body
      )} */}
      <Center>
        <Grid
          gap={"1rem"}
          my={"1rem"}
          flexDir="row"
          gridTemplateColumns={Array(gridColumns).fill("1fr").join(" ")}
        >
          {draggableItems.map((item) =>
            !itemsParent[item.id] ? (
              <GridItem
                key={item.id}
                sx={{
                  width: itemWidth,
                  cursor: "pointer",
                  borderRadius: ".5rem",
                }}
                bg={placeholderBg}
              >
                <Draggable
                  style={{
                    width: itemWidth,
                    height: itemHeight,
                  }}
                  disabled={disabled}
                  onClick={() => handleItemClick(item.id)}
                  id={item.id}
                >
                  {item.label}
                </Draggable>
              </GridItem>
            ) : (
              <GridItem
                key={item.id}
                bg={placeholderBg}
                height={itemHeight}
                borderRadius={".5rem"}
                width={itemWidth}
              ></GridItem>
            )
          )}
        </Grid>
      </Center>
    </DndContext>
  );

  function handleDragStart() {}

  function handleItemClick(itemId: string) {
    if (onDrag) {
      onDrag();
    }
    if (itemsParent[itemId]) {
      setItemsParent((p) => ({ ...p, [itemId]: null }));
      return;
    }

    const emptyContainer = containers.find(
      (containerId) => !Object.values(itemsParent).includes(containerId)
    );

    if (emptyContainer) {
      setItemsParent((prev) => ({
        ...prev,
        [itemId]: emptyContainer,
      }));
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over) {
      setItemsParent((prev) => ({ ...prev, [active.id]: null }));
      return;
    }
    setItemsParent((prev) => {
      const updatedParent = { ...prev };

      // Find the item currently in the target container and set it to null
      const existingItem = Object.keys(prev).find(
        (key) => prev[key] === over.id
      );

      if (existingItem) {
        updatedParent[existingItem] = null;
      }

      // Assign the active item to the target container
      updatedParent[active.id] = over.id;

      return updatedParent;
    });
  }
};

export default DragAndDrop;
